import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

type BarWidth = 'normal' | 'compact';

interface IScrollContainerProps extends React.HTMLProps<HTMLDivElement> {
  barWidth?: BarWidth;
  /**
   * top of the element to make visible
   */
  scrollToY?: number;
  /**
   * how big is the element to make visible? 100% arbitrary default of 1 rem
   */
  scrollToYOffset?: number;
  /**
   * for more reliable multiple scrollTos, pass a target name, otherwise it will only
   * scroll to 1x for a particular passed scrollToY.
   */
  scrollToName?: string;
}

const getClassName = (className?: string, barWidth?: BarWidth) =>
  classNames('dp-scroll-container', className, {
    'dp-scroll-container--compact': barWidth === 'compact',
  });

/**
 * Wrapper for custom scrollbar to encapsulate common bahavior use cases and styles
 *
 * TODO someday will hopefully have option to render scrollbar as overlay.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function ScrollContainer({
  barWidth,
  children,
  className,
  scrollToY,
  scrollToName,
  scrollToYOffset = 16, // 100% arbitrary default of 1 rem
  ...htmlProps
}: IScrollContainerProps) {
  const ref = useRef<HTMLDivElement>(null); //div because there's no HTMLSectionElement type...
  const lastScrollToY = useRef<number | string>(-1);

  useEffect(() => {
    if (
      scrollToY === undefined ||
      !ref.current ||
      scrollToY === -1 ||
      lastScrollToY.current === scrollToName ||
      lastScrollToY.current === scrollToY
    ) {
      if (scrollToName === '') {
        // track that id was cleared if it's defined but falsy
        lastScrollToY.current = '';
      }
      return; // do nothing
    }
    const scrollTop = ref.current.scrollTop;
    const scrollBottom = ref.current.clientHeight + scrollTop;

    // what if we just took an average of top and target and centered it instead?
    // had at least one request on MappingEditor to scroll until a row is just visible, IIRC.
    // this evaluates up or down then gives the min to show top/bottom of the thing:
    let top = scrollToY < scrollTop ? scrollToY : scrollToY + scrollToYOffset;
    if (top < scrollTop) {
      // scrolling up
      ref.current.scrollTo({ top, left: 0, behavior: 'smooth' });
    } else if (top > scrollBottom) {
      // scrolling down, evaluating the amount to scroll
      top -= ref.current.clientHeight;
      ref.current.scrollTo({ top, left: 0, behavior: 'smooth' });
    }
    lastScrollToY.current = scrollToName !== undefined ? scrollToName : scrollToY || -1;
  }, [scrollToName, scrollToY, scrollToYOffset]);

  return (
    <section className={getClassName(className, barWidth)} {...htmlProps} ref={ref}>
      {children}
    </section>
  );
}
